import React, { useState, useContext } from "react";
import { ContentContext } from "../../App";
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Menu Item Component
const SortableMenuItem = ({ item, index, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div
            {...listeners}
            className="cursor-grab hover:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900">{item.label}</h4>
            <p className="text-xs text-gray-500">{item.href}</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">Orden: {item.order}</span>
            {item.is_active ? (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                Activo
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                Inactivo
              </span>
            )}
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(item)}
            className="text-purple-600 hover:text-purple-900 text-sm"
          >
            Editar
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="text-red-600 hover:text-red-900 text-sm"
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

// Menu Item Form Component
const MenuItemForm = ({ item, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    label: '',
    href: '',
    order: 1,
    is_active: true,
    ...item
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        {item ? 'Editar' : 'Nuevo'} Elemento del Menú
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Texto del Menú *
          </label>
          <input
            type="text"
            name="label"
            required
            value={formData.label}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Ej: Inicio, Propiedades, Contacto"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Enlace *
          </label>
          <input
            type="text"
            name="href"
            required
            value={formData.href}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Ej: #inicio, #propiedades, /pagina"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Orden
          </label>
          <input
            type="number"
            name="order"
            min="1"
            value={formData.order}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-700">
            Elemento activo (visible en el menú)
          </label>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 rounded-md p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Consejos para Enlaces</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Para secciones en la misma página: <code>#seccion</code> (ej: #inicio, #propiedades)</li>
          <li>• Para páginas externas: URL completa (ej: https://example.com)</li>
          <li>• Para páginas internas: ruta relativa (ej: /blog, /servicios)</li>
        </ul>
      </div>

      <div className="mt-6 flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
        >
          {item ? 'Actualizar' : 'Crear'} Elemento
        </button>
      </div>
    </form>
  );
};

// Main Menu Manager Component
const MenuManager = () => {
  const { menu, updateMenu, loading } = useContext(ContentContext);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setMenuItems(menu);
  }, [menu]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = menuItems.findIndex(item => item.id === active.id);
      const newIndex = menuItems.findIndex(item => item.id === over.id);
      
      const newItems = arrayMove(menuItems, oldIndex, newIndex);
      
      // Update order values
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        order: index + 1
      }));
      
      setMenuItems(updatedItems);
      
      // Save to backend
      try {
        await updateMenu(updatedItems);
      } catch (error) {
        console.error('Error updating menu order:', error);
        // Revert on error
        setMenuItems(menu);
      }
    }
  };

  const handleSave = async (itemData) => {
    setSaving(true);
    try {
      let updatedItems;
      
      if (editingItem) {
        // Update existing item
        updatedItems = menuItems.map(item => 
          item.id === editingItem.id ? { ...itemData, id: editingItem.id } : item
        );
      } else {
        // Add new item
        const newItem = {
          ...itemData,
          id: Date.now().toString()
        };
        updatedItems = [...menuItems, newItem];
      }

      await updateMenu(updatedItems);
      setShowForm(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving menu item:', error);
      alert('Error al guardar el elemento del menú');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este elemento del menú?')) {
      try {
        const updatedItems = menuItems.filter(item => item.id !== itemId);
        await updateMenu(updatedItems);
      } catch (error) {
        console.error('Error deleting menu item:', error);
        alert('Error al eliminar el elemento del menú');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {editingItem ? 'Editar' : 'Nuevo'} Elemento del Menú
          </h1>
        </div>
        
        <MenuItemForm
          item={editingItem}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión del Menú</h1>
          <p className="text-gray-600">Administra los elementos de navegación de la web</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
        >
          Nuevo Elemento
        </button>
      </div>

      {menuItems.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay elementos en el menú</h3>
          <p className="mt-1 text-sm text-gray-500">
            Comienza agregando un nuevo elemento al menú de navegación.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
            >
              Agregar Elemento
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="mb-4 bg-blue-50 rounded-md p-4">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Arrastra los elementos para reordenar el menú. Los cambios se guardan automáticamente.
            </p>
          </div>

          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={menuItems.map(item => item.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {menuItems.map((item, index) => (
                  <SortableMenuItem
                    key={item.id}
                    item={item}
                    index={index}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Preview Section */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Vista Previa del Menú</h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-yellow-400 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">V</span>
              </div>
              <h1 className="text-xl font-bold text-purple-600">Vivendius</h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              {menuItems.filter(item => item.is_active).map((item) => (
                <span
                  key={item.id}
                  className="text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
                >
                  {item.label}
                </span>
              ))}
            </nav>
          </div>
          <p className="text-sm text-gray-600">
            Así se verá el menú en la web. Solo se muestran los elementos marcados como activos.
          </p>
        </div>
      </div>

      {/* Statistics */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Elementos</p>
              <p className="text-2xl font-bold text-gray-900">{menuItems.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Elementos Activos</p>
              <p className="text-2xl font-bold text-gray-900">
                {menuItems.filter(item => item.is_active).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <svg className="w-8 h-8 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Elementos Inactivos</p>
              <p className="text-2xl font-bold text-gray-900">
                {menuItems.filter(item => !item.is_active).length}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuManager;